import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { CheckCircle, FileText, UserCheck } from 'lucide-react';
import { programsData } from '../data/mock';
import { toast } from 'sonner';

const GetHelp = () => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    program: '',
    situation: '',
    additionalInfo: ''
  });

  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleProgramChange = (value) => {
    setFormData({
      ...formData,
      program: value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
    
    try {
      const response = await fetch(`${BACKEND_URL}/api/applications`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });
      
      if (!response.ok) {
        throw new Error('Failed to submit application');
      }
      
      const data = await response.json();
      console.log('Application submitted:', data);
      
      // Show success state
      setIsSubmitted(true);
      toast.success('Application submitted successfully! We will contact you within 48 hours.');
      
      // Reset form after 3 seconds
      setTimeout(() => {
        setFormData({
          firstName: '',
          lastName: '',
          email: '',
          phone: '',
          program: '',
          situation: '',
          additionalInfo: ''
        });
        setIsSubmitted(false);
      }, 3000);
    } catch (error) {
      console.error('Error submitting application:', error);
      toast.error('Failed to submit application. Please try again or contact us directly.');
    }
  };

  if (isSubmitted) {
    return (
      <div className="min-h-screen pt-20 flex items-center justify-center bg-gradient-to-br from-orange-50 to-amber-50">
        <div className="container mx-auto px-4">
          <Card className="max-w-2xl mx-auto border-0 shadow-2xl">
            <CardContent className="p-12 text-center">
              <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <CheckCircle className="w-12 h-12 text-green-600" />
              </div>
              <h2 className="text-3xl font-bold text-gray-900 mb-4">
                Application Submitted Successfully!
              </h2>
              <p className="text-lg text-gray-600 mb-6">
                Thank you for reaching out. Our intake coordinators will review your application 
                and contact you within 48 hours to discuss next steps.
              </p>
              <div className="bg-orange-50 border-l-4 border-orange-600 p-4 text-left">
                <p className="text-sm text-gray-700">
                  <strong>What happens next?</strong><br />
                  • You'll receive a confirmation email<br />
                  • Our team will review your application<br />
                  • We'll schedule an intake interview<br />
                  • Together, we'll create a personalized support plan
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-20 bg-gradient-to-b from-white to-gray-50">
      {/* Hero Section */}
      <section className="py-16 bg-gradient-to-br from-orange-600 to-amber-600 text-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-5xl md:text-6xl font-bold mb-6">
              We're Here to Help
            </h1>
            <p className="text-xl text-orange-100 leading-relaxed">
              Take the first step toward a brighter future. Complete the application below 
              and our team will connect you with the support you need.
            </p>
          </div>
        </div>
      </section>

      {/* Process Steps */}
      <section className="py-12 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="grid md:grid-cols-3 gap-6">
              <div className="text-center">
                <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <FileText className="w-8 h-8 text-orange-600" />
                </div>
                <h3 className="font-bold text-gray-900 mb-2">1. Submit Application</h3>
                <p className="text-sm text-gray-600">Fill out the form with your information</p>
              </div>
              <div className="text-center">
                <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <UserCheck className="w-8 h-8 text-orange-600" />
                </div>
                <h3 className="font-bold text-gray-900 mb-2">2. Intake Interview</h3>
                <p className="text-sm text-gray-600">Meet with our team to discuss your needs</p>
              </div>
              <div className="text-center">
                <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <CheckCircle className="w-8 h-8 text-orange-600" />
                </div>
                <h3 className="font-bold text-gray-900 mb-2">3. Get Support</h3>
                <p className="text-sm text-gray-600">Begin your journey with personalized care</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Application Form */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto">
            <Card className="border-0 shadow-xl">
              <CardHeader className="bg-gradient-to-r from-orange-50 to-amber-50 border-b">
                <CardTitle className="text-2xl">Service Application Form</CardTitle>
                <p className="text-sm text-gray-600 mt-2">
                  All information is confidential and will only be used to connect you with appropriate services.
                </p>
              </CardHeader>
              <CardContent className="p-8">
                <form onSubmit={handleSubmit} className="space-y-6">
                  {/* Personal Information */}
                  <div>
                    <h3 className="text-lg font-bold text-gray-900 mb-4 pb-2 border-b">
                      Personal Information
                    </h3>
                    <div className="grid md:grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="firstName">First Name *</Label>
                        <Input
                          id="firstName"
                          name="firstName"
                          value={formData.firstName}
                          onChange={handleChange}
                          required
                          placeholder="Enter your first name"
                          className="mt-1"
                        />
                      </div>
                      <div>
                        <Label htmlFor="lastName">Last Name *</Label>
                        <Input
                          id="lastName"
                          name="lastName"
                          value={formData.lastName}
                          onChange={handleChange}
                          required
                          placeholder="Enter your last name"
                          className="mt-1"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Contact Information */}
                  <div>
                    <h3 className="text-lg font-bold text-gray-900 mb-4 pb-2 border-b">
                      Contact Information
                    </h3>
                    <div className="space-y-4">
                      <div>
                        <Label htmlFor="email">Email Address *</Label>
                        <Input
                          id="email"
                          name="email"
                          type="email"
                          value={formData.email}
                          onChange={handleChange}
                          required
                          placeholder="your.email@example.com"
                          className="mt-1"
                        />
                      </div>
                      <div>
                        <Label htmlFor="phone">Phone Number *</Label>
                        <Input
                          id="phone"
                          name="phone"
                          type="tel"
                          value={formData.phone}
                          onChange={handleChange}
                          required
                          placeholder="(555) 123-4567"
                          className="mt-1"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Program Selection */}
                  <div>
                    <h3 className="text-lg font-bold text-gray-900 mb-4 pb-2 border-b">
                      Program Interest
                    </h3>
                    <div>
                      <Label htmlFor="program">Which program are you interested in? *</Label>
                      <Select value={formData.program} onValueChange={handleProgramChange} required>
                        <SelectTrigger className="mt-1">
                          <SelectValue placeholder="Select a program" />
                        </SelectTrigger>
                        <SelectContent>
                          {programsData.map((program) => (
                            <SelectItem key={program.id} value={program.id}>
                              {program.name}
                            </SelectItem>
                          ))}
                          <SelectItem value="unsure">I'm not sure / Need guidance</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  {/* Situation Description */}
                  <div>
                    <h3 className="text-lg font-bold text-gray-900 mb-4 pb-2 border-b">
                      Your Situation
                    </h3>
                    <div className="space-y-4">
                      <div>
                        <Label htmlFor="situation">Briefly describe your current situation *</Label>
                        <Textarea
                          id="situation"
                          name="situation"
                          value={formData.situation}
                          onChange={handleChange}
                          required
                          placeholder="Tell us about your current circumstances and what kind of support you're seeking..."
                          rows={4}
                          className="mt-1"
                        />
                      </div>
                      <div>
                        <Label htmlFor="additionalInfo">
                          Additional Information (Optional)
                        </Label>
                        <Textarea
                          id="additionalInfo"
                          name="additionalInfo"
                          value={formData.additionalInfo}
                          onChange={handleChange}
                          placeholder="Any other information you'd like us to know..."
                          rows={3}
                          className="mt-1"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Privacy Notice */}
                  <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                    <p className="text-xs text-gray-600 leading-relaxed">
                      <strong>Privacy Notice:</strong> Your information is confidential and protected. 
                      We will only use this information to assess your needs and connect you with 
                      appropriate services. We do not share your personal information with third parties 
                      without your consent.
                    </p>
                  </div>

                  {/* Submit Button */}
                  <Button
                    type="submit"
                    size="lg"
                    className="w-full bg-gradient-to-r from-amber-500 to-orange-600 hover:from-amber-600 hover:to-orange-700 text-white font-semibold text-lg shadow-lg hover:shadow-xl transition-all duration-300"
                  >
                    Submit Application
                  </Button>
                </form>
              </CardContent>
            </Card>

            {/* Emergency Resources */}
            <Card className="mt-6 border-2 border-orange-200 bg-orange-50">
              <CardContent className="p-6">
                <h3 className="font-bold text-gray-900 mb-2">Need Immediate Assistance?</h3>
                <p className="text-sm text-gray-700 mb-3">
                  If you're in crisis or need immediate help, please contact:
                </p>
                <ul className="text-sm text-gray-700 space-y-1">
                  <li><strong>Emergency:</strong> 911</li>
                  <li><strong>Suicide Prevention:</strong> 988</li>
                  <li><strong>Our Crisis Line:</strong> (407) 555-HOPE</li>
                </ul>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>
    </div>
  );
};

export default GetHelp;
